import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getTransactionsAction } from 'actions/transactionActions';
import { DATE_FORMATS, DATE_FORMAT_HYPHEN } from 'utilities/constants';
import dayjs from 'dayjs';
import type { FilterValues } from './transactionTypes';
import { ACCOUNT_LEVELS } from './transactionTypes';

/**
 * Transaction API payload structure
 * Documents the expected shape for backend API
 */
interface TransactionAPIPayload {
  contractIdentifier: string;
  grandParentAccountNumber: string;
  accountNumber: string;
  cardNumber: string;
  startDate: string;
  endDate: string;
  contractSearchMethod: 'CONTRACT_NUMBER';
  driverName: string;
  vehicleRegNumber: string;
  sourceNumber: string;
  page: number;
  pageSize: number;
  sortColumn: string;
  sortDirection: boolean;
}

interface UseTransactionsDataProps {
  readonly accountId: string;
  readonly cardNo: string;
  readonly assignedAccountNumber: string;
  readonly assignedGrandfatherClientNumber: string;
  readonly modalType?: string;
  readonly accountLevel?: string;
}

/**
 * Custom hook for managing transactions data fetching
 *
 * RESPONSIBILITIES:
 * - Build API payload from filter values
 * - Dispatch fetch actions
 * - Account level validation (GP/P/C)
 * - State synchronization after fetch
 */
export const useTransactionsData = ({
  accountId,
  cardNo,
  assignedAccountNumber,
  assignedGrandfatherClientNumber,
  modalType,
  accountLevel,
}: UseTransactionsDataProps) => {
  const dispatch = useDispatch();

  /**
   * Build transaction list payload from filter values
   * Transforms UI filter state into API payload format
   */
  const getTransactionsPayload = useCallback(
    (filterValues: FilterValues): TransactionAPIPayload => {
      return {
        contractIdentifier: accountId ?? assignedAccountNumber,
        grandParentAccountNumber: assignedGrandfatherClientNumber,
        accountNumber: accountId ?? filterValues.contractId,
        cardNumber: cardNo ?? filterValues.cardNo,
        startDate: filterValues.startDate ? dayjs(filterValues.startDate, DATE_FORMATS).format(DATE_FORMAT_HYPHEN) : '',
        endDate: filterValues.endDate ? dayjs(filterValues.endDate, DATE_FORMATS).format(DATE_FORMAT_HYPHEN) : '',
        contractSearchMethod: 'CONTRACT_NUMBER',
        driverName: filterValues.driverName?.toUpperCase() ?? '',
        vehicleRegNumber: filterValues.vehicleRegNo?.toUpperCase() ?? '',
        sourceNumber: filterValues.sourceNo,
        page: filterValues.pageNumber,
        pageSize: filterValues.pageSize,
        sortColumn: filterValues.sortColumn,
        sortDirection: filterValues.sortDirection,
      };
    },
    [accountId, cardNo, assignedAccountNumber, assignedGrandfatherClientNumber]
  );

  /**
   * Fetch transaction list with validation
   * Prevents fetching for invalid account levels (GP/P in account modal)
   *
   * @param filterValues - Current filter state
   * @param setFilterValues - State setter to reset trigger flags
   */
  const getTransactionList = useCallback(
    (filterValues: FilterValues, setFilterValues: React.Dispatch<React.SetStateAction<FilterValues>>) => {
      // BUSINESS RULE: Do not fetch transactions for GP or P accounts in account modal
      // Only Contract level (C) accounts should fetch
      const shouldSkipFetch = modalType === 'account' && accountLevel !== ACCOUNT_LEVELS.CONTRACT;

      if (shouldSkipFetch) {
        return;
      }

      const payload = getTransactionsPayload(filterValues);
      dispatch(getTransactionsAction(payload, filterValues.isFilterClick));

      // Reset trigger flags to prevent infinite loops
      setFilterValues((prev) => ({
        ...prev,
        triggerFilter: false,
        isFilterClick: false,
      }));
    },
    [modalType, accountLevel, getTransactionsPayload, dispatch]
  );

  return {
    getTransactionsPayload,
    getTransactionList,
  };
};
